import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MapDashboardPage from "./MapDashboardPage.jsx";
import ListDashboardPage from "./ListDashboardPage.jsx";

const CURRENT_KEY = "dashboard_current_view";
const PREVIOUS_KEY = "dashboard_previous_view";

const DashboardSwitcher = ({ filterData, setFilterData }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [view, setView] = useState(() => {
    if (new URLSearchParams(location.search).has("profileId")) return "map";
    if (location.state?.view === "map" || location.state?.view === "list") return location.state.view;
    return localStorage.getItem(CURRENT_KEY) === "list" ? "list" : "map";
  });
  const [filteredDogs, setFilteredDogs] = useState([]);
  const [isFilterActive, setIsFilterActive] = useState(false);

  const sharedProfileId = new URLSearchParams(location.search).get("profileId");

  useEffect(() => {
    const pending = sessionStorage.getItem("pending_shared_profile");
    if (pending) {
      sessionStorage.removeItem("pending_shared_profile");
      if (!sharedProfileId && (pending.startsWith("/dashboard-switcher?") || pending.startsWith("/profile-details?"))) {
        navigate(pending, { replace: true });
      }
    }
    if (sharedProfileId) setView("map");
  }, [sharedProfileId, navigate]);

  useEffect(() => {
    if (location.state?.view === "map") setView("map");
  }, [location.state?.view]);

  // Restore last opened view on load
  useEffect(() => {
    const saved = localStorage.getItem(CURRENT_KEY);
    if (location.state?.view !== "map" && !new URLSearchParams(location.search).has("profileId") && (saved === "map" || saved === "list")) {
      setView(saved);
    }
  }, []);

  // Intercept Logo click routing state to force Map view and reset filters
  useEffect(() => {
    if (location.state?.resetFilter) {
      // Force the view to map
      setView("map");
      localStorage.setItem(CURRENT_KEY, "map");

      // Reset the filter data strictly to its default
      setFilterData({
        teamSelection: "both",
        proximityValue: 5,
        availability: [""],
        breed: "",
        experience: [""],
      age: "",
      });

      // Clear active filter states
      setIsFilterActive(false);
      setFilteredDogs([]);

      sessionStorage.removeItem("social_paws_isFilterActive");
      sessionStorage.removeItem("social_paws_filteredDogs");
      sessionStorage.removeItem("social_paws_filterData");

      // Clear the location state
      navigate(location.pathname + location.search, { replace: true, state: {} });
    }
  }, [location.state, navigate, setFilterData]);

  // Centralized view switcher
  const switchView = (nextView) => {
    if (nextView === view) return;

    // save previous before changing
    localStorage.setItem(PREVIOUS_KEY, view);
    localStorage.setItem(CURRENT_KEY, nextView);

    setView(nextView);
  };

  // Back handler (Retains current functionality to swap between List/Map)
  const handleBack = () => {
    const previous = localStorage.getItem(PREVIOUS_KEY);

    if (previous === "map" || previous === "list") {
      setView(previous);
    }
  };

  return (
    <>
      {view === "map" && (
        <MapDashboardPage
          filterData={filterData}
          setFilterData={setFilterData}
          filteredDogs={filteredDogs}
          setFilteredDogs={setFilteredDogs}
          isFilterActive={isFilterActive}
          setIsFilterActive={setIsFilterActive}
          onSwitchToList={() => switchView("list")}
          onBack={handleBack}
        />
      )}

      {view === "list" && (
        <ListDashboardPage
          filterData={filterData}
          setFilterData={setFilterData}
          filteredDogs={filteredDogs}
          setFilteredDogs={setFilteredDogs}
          isFilterActive={isFilterActive}
          setIsFilterActive={setIsFilterActive}
          onSwitchToMap={() => switchView("map")}
          onBack={handleBack}
        />
      )}
    </>
  );
};

export default DashboardSwitcher;
